import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Baby, EnvelopeOpen, SignIn } from 'phosphor-react';
import './OnboardingChoice.css';
import { API_BASE_URL } from '../config';

interface OnboardingChoiceProps {
  onAuthSuccess?: (userData: any) => void;
}

const OnboardingChoice: React.FC<OnboardingChoiceProps> = ({ onAuthSuccess }) => {
  const navigate = useNavigate();
  const [isCreating, setIsCreating] = useState(false);

  const handleStartTracking = async () => {
    setIsCreating(true);
    try {
      // Create anonymous user
      const response = await fetch(`${API_BASE_URL}/api/users/anonymous`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const userData = await response.json();
        // Store user in localStorage
        localStorage.setItem('babysleep_user', JSON.stringify(userData));
        // Store a flag to show baby creation form in onboarding
        sessionStorage.setItem('show_create_baby', 'true');
        // Call auth success callback if provided
        if (onAuthSuccess) {
          onAuthSuccess(userData);
        }
        // Navigate to app - onboarding will handle baby creation
        navigate('/');
      } else {
        console.error('Failed to create anonymous user');
        alert('Failed to start. Please try again.');
      }
    } catch (error) {
      console.error('Failed to create anonymous user:', error);
      alert('Failed to start. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleJoinWithCode = async () => {
    setIsCreating(true);
    try {
      // Create anonymous user
      const response = await fetch(`${API_BASE_URL}/api/users/anonymous`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const userData = await response.json();
        // Store user in localStorage
        localStorage.setItem('babysleep_user', JSON.stringify(userData));
        // Store a flag to show invite code input in onboarding
        sessionStorage.setItem('show_invite_input', 'true');
        // Call auth success callback if provided
        if (onAuthSuccess) {
          onAuthSuccess(userData);
        }
        // Navigate to app - onboarding will handle invite acceptance
        navigate('/');
      } else {
        console.error('Failed to create anonymous user');
        alert('Failed to start. Please try again.');
      }
    } catch (error) {
      console.error('Failed to create anonymous user:', error);
      alert('Failed to start. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="onboarding-choice-page">
      <div className="onboarding-choice-container">
        <header className="onboarding-header">
          <h1>Let's Get Started</h1>
          <p>Choose how you'd like to begin</p>
        </header>

        <div className="onboarding-options">
          <button
            className="onboarding-card"
            onClick={handleStartTracking}
            disabled={isCreating}
          >
            <div className="onboarding-icon">
              <Baby size={40} weight="light" />
            </div>
            <h2>Start Tracking</h2>
            <p>Begin tracking your baby's sleep right now</p>
          </button>

          <button
            className="onboarding-card"
            onClick={handleJoinWithCode}
            disabled={isCreating}
          >
            <div className="onboarding-icon">
              <EnvelopeOpen size={40} weight="light" />
            </div>
            <h2>Join with Code</h2>
            <p>Have an invite? Link to existing baby</p>
          </button>

          <button
            className="onboarding-card"
            onClick={() => navigate('/signin')}
            disabled={isCreating}
          >
            <div className="onboarding-icon">
              <SignIn size={40} weight="light" />
            </div>
            <h2>Sign In</h2>
            <p>Already have an account with email?</p>
          </button>
        </div>

        <footer className="onboarding-footer">
          <div className="onboarding-footer-links">
            <Link to="/privacy">Privacy Policy</Link>
            <span className="footer-separator">•</span>
            <Link to="/support">Support</Link>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default OnboardingChoice;
